import { useEffect } from 'react';
import { Modal } from 'antd';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { ModalFuncProps } from 'antd/lib/modal';

interface UsePreventNavigationParams {
  /** if guard active, defaults to `true` */
  prevent?: boolean;
  /** modal `title`, `content` etc.  */
  modalFuncProps?: Partial<ModalFuncProps>;
}
/** Prevent navigating away from current route */
export const usePreventNavigation = ({
  prevent = true,
  modalFuncProps = {},
}: UsePreventNavigationParams = {}) => {
  const history = useHistory();
  const intl = useIntl();

  const _modalFuncProps = modalFuncProps || {};

  useEffect(() => {
    // navigation allowed
    if (!prevent) return;

    // add navigation guard
    const unblock = history.block(({ pathname }, action) => {
      Modal.confirm({
        centered: true,
        title: _modalFuncProps.title || intl.formatMessage({ id: 'you-sure' }),
        content:
          _modalFuncProps.content ||
          intl.formatMessage({ id: 'prevent-navigation-default-message' }),
        onOk: () => {
          unblock(); // allow navigation again

          // delay to hide modal first
          setTimeout(() => {
            // trigger blocked action
            history[historyFnMap[action]](pathname);
          }, 300);
        },
        ..._modalFuncProps,
      });
      return false;
    });

    // cleanup, allow navigation
    return () => unblock();
  }, [prevent, history, _modalFuncProps, intl]);
};

/** Maps navigation action types to `history` functions */
const historyFnMap = {
  PUSH: 'push',
  POP: 'goBack',
  REPLACE: 'replace',
} as const;
